import React, { useEffect, useState, ReactNode, JSX } from 'react';
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';
import { renderIcon } from 'src/constants/icons';
import BaseTour from 'src/components/common/tour/BaseTour';
import TourButton from 'src/components/common/tour/TourButton';
import {
  TourIntroduction,
  TourSection,
  TourParagraph,
  TourList,
  TourListItem,
  TourCompletion,
} from 'src/components/common/tour/TourContentTemplates';
import {
  tourStepStyles,
  checkHeaderTourNewPermissions,
  setTourNavigation,
  shouldStartImmediately,
} from 'src/components/common/tour/tourUtils';

import { HeaderTourProps } from 'src/types/Header';

/**
 * Auto-start at most once per browser session.
 *
 * `HeaderTour` is mounted by every page's `UnifiedHeader`, and the navigation flags it
 * reads (`fromHeaderTour`) are intentionally left in place for the notifications tour,
 * so every navigation re-armed it. The tour button still starts it on demand.
 */
const AUTO_STARTED_KEY = 'headerTourAutoStarted';

const HeaderTour = ({ children, user }: HeaderTourProps): JSX.Element => {
  const navigate = useNavigate();
  const [shouldAutoStart, setShouldAutoStart] = useState(false);

  // Check if user has notification permissions
  const hasNotificationPermissions =
    user?.canUseRealTimeNotificationsFileOwner ||
    user?.canUseRealTimeNotificationsWatched ||
    user?.canUseRealTimeNotificationsProperty;

  // Check if user is a renter (has 'resident' group)
  const isRenter =
    user?.groups &&
    Array.isArray(user.groups) &&
    (user.groups as Array<{ name: string }>).some(
      (group) => group.name.toLowerCase() === 'resident'
    );

  // Handle tour completion - navigate to notifications if user has permissions
  const handleTourEnd = (): void => {
    if (hasNotificationPermissions) {
      // Set flag to indicate coming from header tour
      setTourNavigation('fromHeaderTour');
      // Navigate to notifications page
      navigate('/workbench/notifications');
    }
    // If no notification permissions, tour just ends normally
  };

  // Create dynamic tour steps based on user permissions
  const getTourSteps = (): Array<{
    selector: string;
    content: ReactNode;
    position?: string;
    styles?: Record<string, unknown>;
  }> => {
    const baseSteps = [
      {
        selector: '[data-tour="header-tour-button"]',
        content: (
          <TourIntroduction
            title="Pronto Tools Tour Guide"
            description="We've redesigned the Pronto header to give you quicker access to your most-used tools. This tour will show you what's new!"
            behaviorText=""
            autoPlayText="The tour auto-advances every 20 seconds. Use the X to exit or the navigation buttons to pause and move through steps on your own."
            additionalInfo=""
          />
        ),
      },
      {
        selector: '[data-tour="search-status-notes"]',
        content: (
          <TourSection title="Search & Status Notes">
            <TourParagraph>
              Click the magnifying glass to search for files and view status
              notes.
            </TourParagraph>
            <TourParagraph>
              Search by name, property, or other important information.
            </TourParagraph>
          </TourSection>
        ),
        ...tourStepStyles.headerTourPosition,
      },
    ];

    // Add Watch List step if user has permission
    if (user?.canUseRealTimeNotificationsWatched) {
      baseSteps.push({
        selector: '[data-tour="watchlist-eye"]',
        content: (
          <TourSection title="Watch List">
            <TourParagraph>
              Click the eye icon to see all certification files you&apos;re
              watching.
            </TourParagraph>
            <TourParagraph>
              Watch a file to get notifications about a file even if you&apos;re
              not the file owner.
            </TourParagraph>
            <TourParagraph>
              The number shows how many certifications you&apos;re watching.
            </TourParagraph>
          </TourSection>
        ),
        ...tourStepStyles.headerTourPosition,
      });
    }

    // Add Notification Bell step if user has any notification permissions
    if (
      user?.canUseRealTimeNotificationsFileOwner ||
      user?.canUseRealTimeNotificationsWatched ||
      user?.canUseRealTimeNotificationsProperty
    ) {
      baseSteps.push({
        selector: '[data-tour="notification-bell"]',
        content: (
          <TourSection title="Notifications">
            <TourParagraph>
              Click the bell to see updates across all your certification files
              and properties. The red badge shows how many you haven&apos;t
              read.
            </TourParagraph>
            <TourParagraph>You&apos;ll see:</TourParagraph>
            <Box sx={{ ml: 2 }}>
              <TourList>
                <TourListItem
                  icon={renderIcon('notification', 'email', {
                    style: {
                      color: '#1976d2',
                      fontSize: '20px',
                      marginRight: '8px',
                    },
                  })}
                >
                  <strong>New Communications:</strong> Emails, SMS, and fax
                </TourListItem>
                <TourListItem
                  icon={renderIcon('notification', 'documents', {
                    style: {
                      color: '#1976d2',
                      fontSize: '20px',
                      marginRight: '8px',
                    },
                  })}
                >
                  <strong>Document Updates:</strong> Renter uploaded new
                  documents
                </TourListItem>
                <TourListItem
                  icon={renderIcon('notification', 'documents', {
                    style: {
                      color: '#1976d2',
                      fontSize: '20px',
                      marginRight: '8px',
                    },
                  })}
                >
                  <strong>File Owner Changes:</strong> You were assigned to a
                  certification file
                </TourListItem>
                <TourListItem
                  icon={renderIcon('notification', 'statusNote', {
                    style: {
                      color: '#1976ed',
                      fontSize: '20px',
                      marginRight: '8px',
                    },
                  })}
                >
                  <strong>Status Updates:</strong> New status notes
                </TourListItem>
              </TourList>
            </Box>
          </TourSection>
        ),
        ...tourStepStyles.headerTourPosition,
      });
    }

    // Add Account Avatar step
    baseSteps.push({
      selector: '[data-tour="account-avatar"]',
      content: (
        <TourSection title="Account & Navigation">
          <TourParagraph>
            Click your initial to go to open your user and notification
            preferences or log out.
          </TourParagraph>
        </TourSection>
      ),
      ...tourStepStyles.headerTourPosition,
    });

    // Add final step
    baseSteps.push({
      selector: '[data-tour="header-container"]',
      content: (
        <TourCompletion
          title="Header Tour Complete! 🎉"
          congratulations="You're all set to use the new Pronto Housing header!"
          summary=""
          reminder=""
          nextStep={
            hasNotificationPermissions
              ? 'Next stop: Managing your notifications with the new notifications page.'
              : undefined
          }
        />
      ),
      ...tourStepStyles.headerTourPosition,
    });

    return baseSteps;
  };

  useEffect(() => {
    // Check if user has new permissions (don't check fromHeaderTour as that's for NotificationsTour)
    const hasNewPermissions = checkHeaderTourNewPermissions(user);

    if (hasNewPermissions) {
      // Check if tour should auto-start based on dismissal history
      const canAutoStart = shouldStartImmediately('headerTourData');

      // The tour is mounted by every page's `UnifiedHeader`, and the navigation flags
      // it reads are deliberately left in place for the notifications tour, so every
      // navigation re-armed it: the popover opened over the composer and the first
      // line of any error. Auto-start once per browser session; the tour button is
      // still there for anyone who wants it again.
      const alreadyStarted =
        typeof window !== 'undefined' &&
        window.sessionStorage.getItem(AUTO_STARTED_KEY) === 'true';

      if (canAutoStart && !alreadyStarted) {
        // Add 10-second delay to ensure anchor element is visible before starting tour
        const timer = setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(AUTO_STARTED_KEY, 'true');
          }
          setShouldAutoStart(true);
        }, 10000); // Changed from 5000 to 10000 (10 seconds)

        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  // If user is a renter, don't show the tour at all
  if (isRenter) {
    return <>{children}</>;
  }

  return (
    <BaseTour
      steps={getTourSteps()}
      tourDataKey="headerTourData"
      initialIsOpen={shouldAutoStart}
      onTourEnd={handleTourEnd}
    >
      {children}
    </BaseTour>
  );
};

// Export the TourButton component that uses the context
const HeaderTourButton = (): JSX.Element => (
  <div data-tour="header-tour-button">
    <TourButton title="Start Pronto Tools tour" tourDataKey="headerTourData" />
  </div>
);

export { HeaderTour, HeaderTourButton as TourButton };
export default HeaderTour;
